import {useIntl} from 'react-intl'
import {CONTACT_US_TOPICS, REGEX} from '../../constants'
import {useToast} from '../../hooks/use-toast'
import useCountryList from '../../hooks/use-country-list'
import {useXSSProtection} from './../../hooks/use-xss-protection'

/**
 * Custom hook to generate form fields for a contact us form.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.form - The form object containing control, formState, and setValue.
 * @param {Object} params.form.control - Control object for form management.
 * @param {Object} params.form.formState - Object containing form state, including errors.
 * @param {Object} params.form.formState.errors - Object containing form field errors.
 * @param {Function} params.form.setValue - Function to set form field values.
 * @param {string} [params.prefix='contact_us_'] - A prefix to be used for field names.
 *
 * @returns {Object} An object containing configuration for each form field.
 */
export default function useContactUsFields({
    form: {
        control,
        formState: {errors},
        setValue,
        clearErrors
    },
    prefix = 'contact_us_'
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const countryList = useCountryList()

    const contactUsTopics = [
        {
            value: 'personalDataProtection',
            label: formatMessage(CONTACT_US_TOPICS.personalDataProtection)
        },
        {
            value: 'myAccountNewsletters',
            label: formatMessage(CONTACT_US_TOPICS.myAccountNewsletters)
        },
        {value: 'ordersPayment', label: formatMessage(CONTACT_US_TOPICS.ordersPayment)},
        {value: 'shippingDelivery', label: formatMessage(CONTACT_US_TOPICS.shippingDelivery)},
        {value: 'returnsRefunds', label: formatMessage(CONTACT_US_TOPICS.returnsRefunds)},
        {value: 'repair', label: formatMessage(CONTACT_US_TOPICS.repair)},
        {value: 'customerService', label: formatMessage(CONTACT_US_TOPICS.customerService)},
        {value: 'productsStock', label: formatMessage(CONTACT_US_TOPICS.productsStock)},
        {
            value: 'collaborationOpportunities',
            label: formatMessage(CONTACT_US_TOPICS.collaborationOpportunities)
        },
        {value: 'other', label: formatMessage(CONTACT_US_TOPICS.other)}
    ]

    const {validateField} = useXSSProtection()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'contact_us_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'contact_us_fields.error.required_first_name'
                }),
                validate: (value) => validateField(toast, value, 'contactus-form', 'firstname')
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'contact_us_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'contact_us_fields.error.required_last_name'
                }),
                validate: (value) => validateField(toast, value, 'contactus-form', 'lastname')
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'contact_us_fields.label.email'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'contact_us_fields.error.required_email'
                }),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'use_login_fields.error.invalid_email'
                    })
                },
                validate: (value) => validateField(toast, value, 'contactus-form', 'email')
            },
            error: errors[`${prefix}email`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone',
                id: 'contact_us_fields.label.phone'
            }),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_address_fields.error.please_enter_phone_number'
                }),
                validate: (value) => validateField(toast, value, 'contactus-form', 'phone')
            },
            error: errors[`${prefix}phone`],
            setValue,
            clearErrors,
            control
        },
        orderNumber: {
            name: `${prefix}orderNumber`,
            label: formatMessage({
                defaultMessage: 'Order number',
                id: 'contact_us_fields.label.order_number'
            }),
            type: 'text',
            defaultValue: '',
            rules: {
                validate: (value) => validateField(toast, value, 'contactus-form', 'ordernumber')
            },
            error: errors[`${prefix}orderNumber`],
            control
        },
        country: {
            name: `${prefix}country`,
            label: formatMessage({
                defaultMessage: 'Country',
                id: 'contact_us_fields.label.country_code'
            }),
            type: 'select',
            defaultValue: '',
            options: [
                {
                    value: '',
                    label: formatMessage({
                        id: 'contact_us.countrycode.default',
                        defaultMessage: 'Select your country'
                    })
                },
                ...countryList
            ],
            rules: {
                validate: (value) => validateField(toast, value, 'contactus-form', 'country')
            },
            error: errors[`${prefix}country`],
            control
        },
        question: {
            name: `${prefix}question`,
            label: formatMessage({
                defaultMessage: 'My Question',
                id: 'contact_us_fields.label.question_topic'
            }),
            defaultValue: '',
            type: 'select',
            options: [
                {
                    value: '',
                    label: formatMessage({
                        id: 'contact_us.topics.default',
                        defaultMessage: 'Select a topic'
                    })
                },
                ...contactUsTopics
            ],
            rules: {
                validate: (value) => validateField(toast, value, 'contactus-form', 'question')
            },
            error: errors[`${prefix}question`],
            control
        },
        consent: {
            name: `${prefix}consent`,
            label: formatMessage({
                defaultMessage: `I accept to receive the latest news and promotional offers through the communication means which I have provided, in accordance with Vilebrequin's privacy policy. I can unsubscribe at any time via my online account or by clicking the unsubscribe link that appears in all Vilebrequin communications.`,
                id: 'contact_us_fields.label.consent'
            }),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {
                validate: (value) => validateField(toast, value, 'contactus-form', 'consent')
            },
            error: errors?.[`${prefix}consent`],
            control
        }
    }
    return fields
}
